import { Button } from "@mantine/core";
import { modals } from "@mantine/modals";
import { notifications } from "@mantine/notifications";
import type { JSX } from "react";
import { useMutation } from "urql";
import ItemForm, { type IFormValues } from "./ItemForm";
import { addItemMutation, type TuneThreadAddItemMutation, type TuneThreadAddItemMutationVariables } from "./queries";

interface IProps {
    tuneThread: {
        id: number;
    };
    disabled: boolean;
}

function AddItemButton({ tuneThread, disabled }: IProps): JSX.Element {
    const [addResult, executeAdd] = useMutation<TuneThreadAddItemMutation, TuneThreadAddItemMutationVariables>(
        addItemMutation,
    );

    const add = async (values: IFormValues) => {
        const isMessage = values.type === "MESSAGE";
        const isFunctionCall = values.type === "FUNCTION_CALL";
        const isFunctionOutput = values.type === "FUNCTION_CALL_OUTPUT";

        const data: TuneThreadAddItemMutationVariables["data"] = {
            tuneThreadId: tuneThread.id,
            item: {
                type: values.type,
                role: isMessage ? values.role : null,
                content: values.content,
                imageB64: isMessage ? values.imageB64 : null,
                imageName: isMessage ? values.imageName || null : null,
                toolCallId: isFunctionCall || isFunctionOutput ? values.toolCallId || null : null,
                toolName: isFunctionCall ? values.toolName || null : null,
                toolArguments:
                    isFunctionCall && Object.keys(values.toolArguments).length > 0
                        ? JSON.stringify(values.toolArguments)
                        : null,
                reasoningSummary: [],
                encryptedReasoning: null,
                reasoningId: null,
                context: false,
            },
        };

        const result = await executeAdd({ data });
        if (result.error) {
            notifications.show({
                title: "Failed to add item",
                message: result.error.message,
            });
        } else {
            notifications.show({
                title: "Item added",
                message: "Item added to thread",
            });
        }
    };

    const onClick = () =>
        modals.open({
            title: "Add item to thread",
            size: "800px",
            children: (
                <ItemForm
                    fetching={addResult.fetching}
                    handleSubmit={(values) => {
                        add(values);
                        modals.closeAll();
                    }}
                />
            ),
        });

    return (
        <Button onClick={onClick} disabled={disabled || addResult.fetching}>
            Add item
        </Button>
    );
}

export default AddItemButton;
